import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import {
  fetchAlerts,
  createAlert,
  updateAlert,
  deleteAlert,
  fetchTickers,
} from '../api/client';
import LoginModal from '../components/LoginModal';
import './Alerts.css';

function Alerts() {
  const { isGuest, user } = useAuth();
  const { sendNotification } = useNotifications();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [formData, setFormData] = useState({
    crypto: '',
    symbol: '',
    condition: 'above',
    price: '',
  });


  const [availableCoins, setAvailableCoins] = useState([]);
  const [showCoinSuggestions, setShowCoinSuggestions] = useState(false);
  const [coinSearch, setCoinSearch] = useState('');


  useEffect(() => {
    if (user && !isGuest) {
      loadAlerts();
    } else {
      setAlerts([]);
    }
  }, [user, isGuest]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await fetchAlerts();
      setAlerts(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load alerts:', err);
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const loadCoinsFromTickers = async () => {
      try {
        const data = await fetchTickers({ limit: 2000 });

        if (!data?.results) {
          setAvailableCoins([]);
          return;
        }

        const map = new Map();

        data.results.forEach((ticker) => {
          const [base] = (ticker.market_symbol || '').split('/');
          if (!base) return;

          const symbol = base.toUpperCase();
          if (!map.has(symbol)) {
            map.set(symbol, {
              name: symbol,
              symbol: symbol,
            });
          }
        });

        setAvailableCoins(Array.from(map.values()));
      } catch (err) {
        console.error('Failed to load coins:', err);
        setAvailableCoins([]);
      }
    };

    loadCoinsFromTickers();
  }, []);

  const handleCreate = () => {
    if (isGuest) {
      setShowLoginModal(true);
      return;
    }
    setEditingAlert(null);
    setFormData({ crypto: '', symbol: '', condition: 'above', price: '' });
    setShowForm(true);
  };

  const handleEdit = (alert) => {
    if (isGuest) {
      setShowLoginModal(true);
      return;
    }
    setEditingAlert(alert);
    setFormData({
      crypto: alert.crypto,
      symbol: alert.symbol,
      condition: alert.condition,
      price: alert.price.toString(),
    });
    setShowForm(true);
  };


  const filteredCoins = availableCoins.filter((coin) => {
    if (!coinSearch) return true;
    const q = coinSearch.toLowerCase();
    return (
      coin.name.toLowerCase().includes(q) ||
      coin.symbol.toLowerCase().includes(q)
    );
  });

  const handleCryptoChange = (e) => {
    const value = e.target.value;
    setCoinSearch(value);
    setFormData((prev) => ({
      ...prev,
      crypto: value,
    }));
    setShowCoinSuggestions(true);
  };

  const handleSelectCoin = (coin) => {
    setFormData((prev) => ({
      ...prev,
      crypto: coin.name,
      symbol: coin.symbol,
    }));
    setCoinSearch(coin.name);
    setShowCoinSuggestions(false);
  };

  const handleSave = async () => {
    if (!formData.crypto || !formData.symbol || !formData.price) {
      alert('Ве молиме пополнете ги сите полиња');
      return;
    }

    const priceValue = parseFloat(formData.price);
    if (isNaN(priceValue) || priceValue <= 0) {
      alert('Ве молиме внесете валидна цена');
      return;
    }


    const matchingCoin = availableCoins.find(
      (c) =>
        c.symbol.toUpperCase() === formData.symbol.toUpperCase() ||
        c.name.toLowerCase() === formData.crypto.toLowerCase()
    );

    if (!matchingCoin) {
      alert('Оваа криптовалута не постои во системот.');
      return;
    }

    const payload = {
      ...formData,
      crypto: matchingCoin.name,
      symbol: matchingCoin.symbol,
      price: priceValue,
    };

    try {
      if (editingAlert) {
        const updatedAlert = await updateAlert(editingAlert.id, payload);
        setAlerts(alerts.map((a) => (a.id === editingAlert.id ? updatedAlert : a)));
      } else {
        const newAlert = await createAlert(payload);
        setAlerts([newAlert, ...alerts]);
      }

      setShowForm(false);
      setEditingAlert(null);
      setFormData({ crypto: '', symbol: '', condition: 'above', price: '' });
    } catch (err) {
      console.error('Failed to save alert:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to save alert';
      alert(`Грешка при зачувување: ${msg}`);
    }
  };

  const handleDelete = async (id) => {
    if (isGuest) {
      setShowLoginModal(true);
      return;
    }
    if (window.confirm('Дали сте сигурни дека сакате да го избришете ова предупредување?')) {
      try {
        await deleteAlert(id);
        setAlerts(alerts.filter((a) => a.id !== id));
      } catch (err) {
        console.error('Failed to delete alert:', err);
        alert('Failed to delete alert');
      }
    }
  };

  const handleToggle = async (id) => {
    const alert = alerts.find((a) => a.id === id);
    try {
      const updatedAlert = await updateAlert(id, { ...alert, active: !alert.active });
      setAlerts(alerts.map((a) => (a.id === id ? updatedAlert : a)));
    } catch (err) {
      console.error('Failed to toggle alert:', err);
    }
  };


  useEffect(() => {
    const checkAlerts = setInterval(async () => {
      try {
        const data = await fetchTickers({ limit: 2000 });
        if (!data?.results) return;

        const priceMap = new Map();
        data.results.forEach((t) => {
          if (t.exchange && t.exchange.name) {
            priceMap.set(t.exchange.name.toUpperCase(), t.last);
          }
          // Also map by symbol part if needed, e.g. "BTC/USD" -> "BTC"
          const parts = (t.market_symbol || '').split('/');
          if (parts[0]) {
            priceMap.set(parts[0].toUpperCase(), t.last);
          }
        });

        alerts.forEach((alert) => {
          if (alert.active) {
            const currentPrice = priceMap.get(alert.symbol.toUpperCase());

            if (currentPrice !== undefined) {
              if (
                (alert.condition === 'above' && currentPrice >= alert.price) ||
                (alert.condition === 'below' && currentPrice <= alert.price)
              ) {
                const userEmail = user?.email;
                // Only send if we haven't sent recently? 
                // The backend also checks timing, but frontend is calling API. 
                // Ideally this logic moves to backend, but for now we fix frontend.

                sendNotification(
                  'alert',
                  `Цената на ${alert.crypto} (${alert.symbol}) достигна ${alert.condition === 'above' ? 'над' : 'под'
                  } $${alert.price.toLocaleString()}`,
                  alert.crypto,
                  currentPrice,
                  {
                    email: userEmail,
                    symbol: alert.symbol,
                    condition: alert.condition,
                    targetPrice: alert.price,
                    current_price: currentPrice
                  }
                );
              }
            }
          }
        });
      } catch (err) {
        console.error("Error checking alerts:", err);
      }
    }, 60000); // Check every minute instead of 30s to reduce load

    return () => clearInterval(checkAlerts);
  }, [alerts, sendNotification, user]);

  return (
    <div className="alerts-page">
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message="Најавете се за да креирате и управувате со предупредувања"
      />

      <div className="alerts-header">
        <h1>Предупредувања</h1>
        <p className="muted">Управувајте со вашите ценовни предупредувања</p>
        {isGuest && (
          <div className="guest-notice">
            <p>Како гостин, можете да ги прегледате предупредувањата, но за да креирате или уредувате, треба да се најавите.</p>
          </div>
        )}
        {!isGuest && user && (!user.email || !user.email.trim()) && (
          <div className="guest-notice" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', color: '#856404' }}>
            <p>
              <strong>⚠ Предупредување:</strong> Немате поставено е-пошта адреса. 
              За да примате известувања по е-пошта кога ќе се активираат вашите предупредувања, 
              ве молиме <a href="/profile" style={{ color: '#856404', textDecoration: 'underline' }}>додадете е-пошта во вашиот профил</a>.
            </p>
          </div>
        )}
      </div>

      <div className="alerts-actions">
        <button className="button button-primary" onClick={handleCreate}>
          + Креирај ново предупредување
        </button>
      </div>

      {showForm && (
        <div className="alert-form-card">
          <h2>{editingAlert ? 'Уреди предупредување' : 'Ново предупредување'}</h2>
          <div className="form-grid">


            <div className="form-group">
              <label>Криптовалута:</label>
              <input
                type="text"
                value={formData.crypto}
                onChange={handleCryptoChange}
                onFocus={() => setShowCoinSuggestions(true)}
                placeholder="BTC, ETH..."
              />
              {showCoinSuggestions && (
                <div className="coin-suggestions">
                  {filteredCoins.length === 0 ? (
                    <div className="coin-suggestion-item empty">
                      Нема пронајдени монети
                    </div>
                  ) : (
                    filteredCoins.map((coin) => (
                      <div
                        key={coin.symbol}
                        className="coin-suggestion-item"
                        onClick={() => handleSelectCoin(coin)}
                      >
                        {coin.name} ({coin.symbol})
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Симбол:</label>
              <input
                type="text"
                value={formData.symbol}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    symbol: e.target.value.toUpperCase(),
                  })
                }
                placeholder="BTC"
              />
            </div>

            <div className="form-group">
              <label>Услов:</label>
              <select
                value={formData.condition}
                onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
              >
                <option value="above">Над</option>
                <option value="below">Под</option>
              </select>
            </div>

            <div className="form-group">
              <label>Цена ($):</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="65000"
                step="0.01"
              />
            </div>
          </div>

          <div className="form-actions">
            <button className="button button-primary" onClick={handleSave}>
              Зачувај
            </button>
            <button
              className="button button-outline"
              onClick={() => {
                setShowForm(false);
                setEditingAlert(null);
              }}
            >
              Откажи
            </button>
          </div>
        </div>
      )}

      <div className="alerts-list">
        {alerts.length === 0 ? (
          <div className="empty-state">
            <p>Нема креирани предупредувања</p>
          </div>
        ) : (
          alerts.map((alert) => (
            <div key={alert.id} className="alert-card">
              <div className="alert-info">
                <div className="alert-crypto">
                  <strong>{alert.crypto}</strong> ({alert.symbol})
                </div>
                <div className="alert-condition">
                  Предупредување кога цената е{' '}
                  <strong>{alert.condition === 'above' ? 'над' : 'под'}</strong>{' '}
                  ${alert.price.toLocaleString()}
                </div>
                <div className="alert-meta">
                  Креирано: {new Date(alert.created_at).toLocaleString('mk-MK')}
                </div>
              </div>

              <div className="alert-status">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={alert.active}
                    onChange={() => handleToggle(alert.id)}
                  />
                  <span className="toggle-slider" />
                </label>
                <span className={alert.active ? 'status-active' : 'status-inactive'}>
                  {alert.active ? 'Активно' : 'Неактивно'}
                </span>
              </div>

              <div className="alert-actions">
                <button
                  className="button button-outline"
                  onClick={() => handleEdit(alert)}
                >
                  Уреди
                </button>
                <button
                  className="button button-outline delete-btn"
                  onClick={() => handleDelete(alert.id)}
                >
                  Избриши
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Alerts;
